import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { BookOpen, ArrowRight, Loader2, Keyboard } from 'lucide-react';
import { toast } from 'sonner';
import { projectId, publicAnonKey } from '../../utils/supabase/info';
import { createStudentSession } from '../../utils/supabase/auth';

export default function StudentJoin() {
  const navigate = useNavigate();
  const [sessionCode, setSessionCode] = useState('');
  const [studentName, setStudentName] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'code' | 'name'>('code');

  const handleJoin = async () => {
    if (!sessionCode.trim()) {
      toast.error('Please enter a session code');
      return;
    }
    if (!studentName.trim()) {
      toast.error('Please enter your name');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-9d7d881a/student/login`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${publicAnonKey}`
          },
          body: JSON.stringify({
            fullName: studentName.trim(),
            sessionCode: sessionCode.toUpperCase().trim()
          })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to join session');
      }

      createStudentSession(data.studentName, data.session.id, data.session.sessionCode);
      toast.success(`Welcome, ${data.studentName}!`);

      setTimeout(() => {
        navigate(`/student/live/${data.session.id}`);
      }, 500);
    } catch (error) {
      console.error('Join session error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Could not join session. Please check your code and try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
    setSessionCode(value);
  };

  const handleNext = () => {
    if (!sessionCode.trim()) {
      toast.error('Please enter a session code');
      return;
    }
    setStep('name');
  };

  const canJoin = sessionCode.trim().length > 0 && studentName.trim().length > 0;

  return (
    <div className="fixed inset-0 bg-[#f8f9fa] flex flex-col">
      {/* Top Bar */}
      <header className="flex items-center justify-between px-4 lg:px-6 h-14 lg:h-16 flex-shrink-0 border-b border-[#e0e0e0] bg-white">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2.5 hover:opacity-80 transition-opacity"
        >
          <img
            src="/edulive-logo.png"
            alt="EduLive"
            className="w-8 h-8 lg:w-9 lg:h-9 object-contain"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              const fallback = e.currentTarget.nextElementSibling as HTMLElement;
              if (fallback) fallback.style.display = 'flex';
            }}
          />
          <div
            className="w-8 h-8 lg:w-9 lg:h-9 rounded-lg bg-primary items-center justify-center hidden"
          >
            <BookOpen className="w-5 h-5 text-white" />
          </div>
          <span className="text-base lg:text-lg font-semibold text-[#202124]">
            EduChemMix <span className="text-muted-foreground font-normal text-sm">JHS</span>
          </span>
        </button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/')}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          Back to Home
        </Button>
      </header>

      {/* Main Content - Centered */}
      <main className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          {/* Card */}
          <div className="bg-white rounded-2xl border border-[#e0e0e0] shadow-sm px-8 py-8 lg:px-10 lg:py-10">
            {/* Step: Code */}
            {step === 'code' ? (
              <>
                <div className="text-center mb-8">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Keyboard className="w-6 h-6 text-primary" />
                  </div>
                  <h1 className="text-2xl font-normal text-[#202124] mb-1">
                    Enter a session code
                  </h1>
                  <p className="text-sm text-[#5f6368]">
                    Ask your teacher for the code, then enter it here
                  </p>
                </div>

                <div className="space-y-6">
                  <div>
                    <Input
                      id="session-code-input"
                      type="text"
                      value={sessionCode}
                      onChange={handleCodeChange}
                      placeholder="Session code"
                      className="h-12 text-center text-lg font-mono tracking-[0.3em] rounded-md border border-[#dadce0] bg-white focus:border-primary focus:ring-1 focus:ring-primary placeholder:tracking-normal placeholder:font-sans placeholder:text-sm"
                      maxLength={20}
                      disabled={loading}
                      autoFocus
                      onKeyDown={(e) => e.key === 'Enter' && handleNext()}
                    />
                  </div>

                  <Button
                    onClick={handleNext}
                    className="w-full h-11 rounded-full text-sm font-medium"
                    disabled={!sessionCode.trim() || loading}
                  >
                    Next
                    <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Button>
                </div>
              </>
            ) : (
              /* Step: Name */
              <>
                <div className="text-center mb-8">
                  <div className="inline-flex items-center gap-2 bg-[#f1f3f4] rounded-full pl-3 pr-4 py-1.5 mb-5">
                    <span className="font-mono text-sm text-[#202124] tracking-wider">{sessionCode}</span>
                    <button
                      onClick={() => setStep('code')}
                      className="text-xs text-primary font-medium hover:underline ml-1"
                    >
                      Change
                    </button>
                  </div>
                  <h1 className="text-2xl font-normal text-[#202124] mb-1">
                    What's your name?
                  </h1>
                  <p className="text-sm text-[#5f6368]">
                    Your teacher will see this name
                  </p>
                </div>

                <div className="space-y-6">
                  <div>
                    <Input
                      id="student-name-input"
                      type="text"
                      value={studentName}
                      onChange={(e) => setStudentName(e.target.value)}
                      placeholder="Your full name"
                      className="h-12 text-base rounded-md border border-[#dadce0] bg-white focus:border-primary focus:ring-1 focus:ring-primary placeholder:text-sm"
                      onKeyDown={(e) => e.key === 'Enter' && canJoin && !loading && handleJoin()}
                      disabled={loading}
                      autoFocus
                    />
                  </div>

                  <div className="flex gap-3">
                    <Button
                      variant="ghost"
                      onClick={() => setStep('code')}
                      className="flex-1 h-11 rounded-full text-sm font-medium text-[#5f6368] hover:bg-[#f1f3f4]"
                      disabled={loading}
                    >
                      Back
                    </Button>
                    <Button
                      onClick={handleJoin}
                      className="flex-1 h-11 rounded-full text-sm font-medium"
                      disabled={!canJoin || loading}
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Joining...
                        </>
                      ) : (
                        'Join'
                      )}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Help Text */}
          <p className="text-center text-xs text-[#5f6368] mt-5">
            No session code?{' '}
            <button
              onClick={() => navigate('/')}
              className="text-primary hover:underline font-medium"
            >
              Learn more
            </button>{' '}
            about EduChemMix
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="flex items-center justify-center px-4 h-12 flex-shrink-0 border-t border-[#e0e0e0] bg-white">
        <p className="text-xs text-[#5f6368]">
          EduChemMix-JHS &copy; 2026. All rights reserved.
        </p>
      </footer>
    </div>
  );
}